import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useGeneration } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Pencil, LayoutTemplate, ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function GenerationDetail() {
  const { id } = useParams()
  const { data: generation, isLoading, error } = useGeneration(id!)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [generatedLightboxIndex, setGeneratedLightboxIndex] = useState<number | null>(null)

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!generation) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/generations">생성된 이미지</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">생성된 이미지 상세</h1>
        </div>
        <Link to={`/admin/generations/${id}/edit`}>
          <Button>
            <Pencil className="w-4 h-4 mr-2" />
            수정
          </Button>
        </Link>
      </div>

      {/* Deleted Banner */}
      {generation.deletedAt && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <Badge variant="destructive">삭제됨</Badge>
          <span className="text-sm text-red-800">
            {new Date(generation.deletedAt).toLocaleString('ko-KR')} 에 삭제됨
          </span>
        </div>
      )}

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">사용자 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{generation.userId}</div>
            {generation.user?.email && (
              <div className="text-xs text-gray-400 mt-1">{generation.user.email}</div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">스타일</label>
            <div className="mt-1 text-gray-900">{generation.style || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{generation.aspectRatio}</div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">템플릿</label>
            {generation.template ? (
              <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {generation.template.thumbnailUrl ? (
                  <img
                    src={generation.template.thumbnailUrl}
                    alt={generation.template.name}
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                    <LayoutTemplate className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <Link
                    to={`/admin/templates/${generation.template.id}`}
                    className="font-medium text-gray-900 hover:text-primary transition-colors"
                  >
                    {generation.template.name}
                  </Link>
                  {generation.template.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{generation.template.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">모델</label>
            <div className="mt-1 text-gray-900">{generation.modelId}</div>
          </div>

          {generation.generatedImages && generation.generatedImages.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">이미지 개수</label>
              <div className="mt-1 text-gray-900">{generation.generatedImages.length}개</div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">생성일시</label>
            <div className="mt-1 text-gray-900 text-sm">
              {new Date(generation.createdAt).toLocaleString('ko-KR')}
            </div>
          </div>

          {generation.referenceImages && generation.referenceImages.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">참조 이미지</label>
              <div className="mt-1 text-gray-900">{generation.referenceImages.length}개</div>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {generation.prompt}
          </div>
        </div>

        {generation.referenceImages && generation.referenceImages.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-4">
              참조 이미지 ({generation.referenceImages.length}개)
            </label>
            <div className="grid grid-cols-4 gap-4">
              {generation.referenceImages.map((ref, idx) => (
                <div
                  key={idx}
                  className="bg-gray-100 rounded-lg overflow-hidden cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                  onClick={() => setLightboxIndex(idx)}
                >
                  <img
                    src={ref.thumbnailUrl}
                    alt={`참조 이미지 ${idx + 1}`}
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* Reference Image Lightbox */}
            <Dialog
              open={lightboxIndex !== null}
              onOpenChange={(open) => {
                if (!open) setLightboxIndex(null)
              }}
            >
              <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
                <DialogTitle className="sr-only">참조 이미지 미리보기</DialogTitle>
                {lightboxIndex !== null && generation.referenceImages && (
                  <div className="relative flex items-center justify-center">
                    <img
                      src={generation.referenceImages[lightboxIndex].imageUrl}
                      alt={`참조 이미지 ${lightboxIndex + 1}`}
                      className="max-h-[80vh] w-auto rounded-lg object-contain"
                    />

                    {/* Close */}
                    <button
                      onClick={() => setLightboxIndex(null)}
                      className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    {/* Prev */}
                    {generation.referenceImages.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setLightboxIndex(
                            (lightboxIndex - 1 + generation.referenceImages!.length) %
                              generation.referenceImages!.length,
                          )
                        }}
                        className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}

                    {/* Next */}
                    {generation.referenceImages.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setLightboxIndex(
                            (lightboxIndex + 1) % generation.referenceImages!.length,
                          )
                        }}
                        className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    )}

                    {/* Counter */}
                    {generation.referenceImages.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                        {lightboxIndex + 1} / {generation.referenceImages.length}
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Generated Images */}
      {generation.generatedImages && generation.generatedImages.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            생성 이미지 ({generation.generatedImages.length}개)
          </label>
          <div className="grid grid-cols-2 gap-4">
            {generation.generatedImages.map((img, idx) => (
              <div key={idx} className="space-y-2">
                <img
                  src={img.imageUrl}
                  alt={`생성된 이미지 ${idx + 1}`}
                  className="w-full rounded-lg border shadow-lg cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                  loading="lazy"
                  onClick={() => setGeneratedLightboxIndex(idx)}
                />
                <div className="text-xs text-gray-500 font-mono text-center">
                  seed: {img.seed}
                </div>
              </div>
            ))}
          </div>

          {/* Generated Image Lightbox */}
          <Dialog
            open={generatedLightboxIndex !== null}
            onOpenChange={(open) => {
              if (!open) setGeneratedLightboxIndex(null)
            }}
          >
            <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
              <DialogTitle className="sr-only">생성된 이미지 미리보기</DialogTitle>
              {generatedLightboxIndex !== null && generation.generatedImages && (
                <div className="relative flex items-center justify-center">
                  <img
                    src={generation.generatedImages[generatedLightboxIndex].imageUrl}
                    alt={`생성된 이미지 ${generatedLightboxIndex + 1}`}
                    className="max-h-[80vh] w-auto rounded-lg object-contain"
                  />

                  {/* Close */}
                  <button
                    onClick={() => setGeneratedLightboxIndex(null)}
                    className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Prev */}
                  {generation.generatedImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setGeneratedLightboxIndex(
                          (generatedLightboxIndex - 1 + generation.generatedImages!.length) %
                            generation.generatedImages!.length,
                        )
                      }}
                      className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}

                  {/* Next */}
                  {generation.generatedImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setGeneratedLightboxIndex(
                          (generatedLightboxIndex + 1) % generation.generatedImages!.length,
                        )
                      }}
                      className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}

                  {/* Counter */}
                  {generation.generatedImages.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                      {generatedLightboxIndex + 1} / {generation.generatedImages.length}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Usage Info */}
      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">사용량 정보</label>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">비용 (KRW)</label>
            <div className="mt-1 text-gray-900 font-mono">
              {generation.costKrw?.toLocaleString('ko-KR') ?? 0}원
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Usage Log ID</label>
            <div className="mt-1 text-gray-900 font-mono">
              {generation.usageLogId || '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
